import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import { NgOptimizedImage } from "@angular/common";
import {PaginatorComponent} from "../../../../layout/paginator/paginator.component";
import {TableSorterHeaderDirective} from "../../../../../directives/table-sorter/table-sorter-header.directive";
import {Paginator} from '../../../../../tools/pagination/paginator';
import {TableSorter} from '../../../../../tools/table-sorter/table-sorter';
import {TableSorterDirective} from '../../../../../directives/table-sorter/table-sorter.directive';
import {LoaderService} from '../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {ConfirmDirective} from '../../../../../directives/confirm/confirm.directive';
import {RouterLink} from '@angular/router';
import {CountryService} from '../../../../../services/country/country.service';
import {IsGranted} from '../../../../../decorator/is-granted';
import {IsGrantedDirective} from '../../../../../directives/is-granted/is-granted.directive';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {Site} from '../../../../../models/site/site';
import {SiteService} from '../../../../../services/site/site.service';
import {LangService} from '../../../../../services/language/lang.service';
import {FormsModule} from '@angular/forms';
import {debounceTime, forkJoin, Subject} from 'rxjs';
import {Product} from '../../../../../models/product';
import {ProductService} from '../../../../../services/product/product.service';
import {Advertiser} from '../../../../../models/advertiser';
import {AdvertiserService} from '../../../../../services/advertiser/advertiser.service';
import {Language} from '../../../../../models/language';
import {DropdownSelectComponent} from '../../../../layout/dropdown-select/dropdown-select.component';
import {
  DropdownSelectOptionComponent
} from '../../../../layout/dropdown-select-option/dropdown-select-option.component';
import {ListFilters} from '../../../../../tools/list-filters/list-filters';
import {ListFilterService} from '../../../../../services/list-filter/list-filter.service';

@Component({
  selector: 'app-site',
  imports: [
    PaginatorComponent,
    TableSorterHeaderDirective,
    TableSorterDirective,
    ConfirmDirective,
    RouterLink,
    IsGrantedDirective,
    NgOptimizedImage,
    FormsModule,
    DropdownSelectComponent,
    DropdownSelectOptionComponent
],
  templateUrl: './site-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './site-list.component.scss'
})
@IsGranted(['SITE_VIEW'])
@MetaTitle('Sites')
export class SiteListComponent implements OnInit {

  sites: Site[] = [];
  products: Product[] = [];
  advertisers: Advertiser[] = [];
  languages: Language[] = [];
  paginator: Paginator = new Paginator();
  tableSorter: TableSorter = new TableSorter('id', 'asc');

  filters: ListFilters;

  private nameChange$ = new Subject<void>();

  constructor(
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private siteService: SiteService,
    private productService: ProductService,
    private advertiserService: AdvertiserService,
    protected countryService: CountryService,
    protected langService: LangService,
    private listFilterService: ListFilterService,
  ) {
    this.filters = listFilterService.createFilters({
      name: 'string',
      productId: 'number[]',
      advertiserId: 'number[]',
      lang: 'string[]',
    }, this.paginator, this.tableSorter);
  }

  ngOnInit(): void {
    this.loader.startLoading();

    forkJoin([
      this.productService.findAll({sort: 'name'}),
      this.advertiserService.findAll({sort: 'companyName'}),
      this.langService.getLanguages(),
    ]).subscribe(([products, advertisers, languages]) => {
      this.products = products;
      this.advertisers = advertisers;
      this.languages = languages;

      this.loader.stopLoading();
    });

    this.nameChange$.pipe(debounceTime(1000)).subscribe(() => this.filters.onFilterChange());

    this.filters.init(() => this.fetchAll());
  }

  onNameChange(): void {
    this.nameChange$.next();
  }

  refresh(): void {
    this.fetchAll();
  }

  fetchAll(): void {
    this.loader.startLoading();

    const params: any = {groups: 'product,advertiser', ...this.filters.toParams()};

    this.siteService.findAll(params, this.paginator, this.tableSorter).subscribe((sites) => {
      this.sites = sites;

      this.loader.stopLoading();
    })
  }

  delete(site: Site): void {
    if (site.id) {
      this.loader.startLoading();
      this.siteService.delete(site.id).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Site has been deleted');
        this.refresh();
      });
    }
  }
}
